import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { WebsitesAndAppsListModal } from "@/components/WebsitesAndAppsListModal";
import StudentTileTabItem from "@/js/components/StudentTile/components/activeScreenStates/StudentTileTabItem";
import { BackgroundTab, isBeingRestricted, WindowData } from "@/js/components/RegularClassroomUser/utils";
import { UserInfo } from "@/js/api/authApi";
import { User } from "@/js/utils/userUtils";

interface StudentTileTabViewProps {
	windowData: WindowData;
	user: User;
	tabItemLimit: number;
	canCloseTab: boolean;
	isClassActive: boolean;
	isMonitoring: boolean;
	isStudentClaimed: boolean;
	forceCloseTab: (tabId: string, setLoading: boolean) => void;
	openP2pModal?: (user: UserInfo, tabId: string, chromeId: string, windowId: string, isActiveTab: boolean) => void;
}

const StudentTileTabView: FC<StudentTileTabViewProps> = (props: StudentTileTabViewProps) => {
	const { t } = useTranslation();
	// states to handle tab list view modal
	const [listModalTitle, setListModalTitle] = useState("");
	const [listModalItems, setListModalItems] = useState<BackgroundTab[]>([]);
	const [listModalIsMonitorMode, setListModalIsMonitorMode] = useState(false);
	const [listModalOpen, setListModalOpen] = useState(false);

	// tab list view modal handler
	const openListModal = (title: string, items: BackgroundTab[] | undefined, isMonitorMode: boolean) => {
		setListModalTitle(title);
		setListModalItems(items ?? []);
		setListModalIsMonitorMode(isMonitorMode);
		setListModalOpen(true);
	};

	const closeListModal = () => {
		setListModalOpen(false);
	};

	const backgroundTabs = props.windowData.background_tabs
		? props.windowData.background_tabs.map((bt) => ({
				url: bt.tabUrl,
				favicon: bt.favIcon,
				title: bt.title,
				tab_id: bt.tab_id,
			}))
		: [];

	const tabsData = [props.windowData, ...backgroundTabs];
	const tabsToDisplay = tabsData.slice(0, props.tabItemLimit);

	return (
		<div className="StudentTileContent_tabsList">
			{tabsToDisplay.map((tab, i) => (
				<StudentTileTabItem
					key={`${tab.tab_id || tab.url}-${i}`}
					item={tab}
					chromeId={props.windowData.chrome_id}
					chromeWindowId={props.windowData.chrome_window_id}
					user={props.user}
					forceCloseTab={props.forceCloseTab}
					canCloseTab={props.canCloseTab}
					classIsActive={props.isClassActive}
					openP2pModal={props.openP2pModal}
					isMonitorMode={props.isMonitoring}
					isStudentClaimed={props.isStudentClaimed}
					isRestricted={isBeingRestricted(props.windowData)}
				/>
			))}
			<WebsitesAndAppsListModal
				title={listModalTitle}
				open={listModalOpen}
				activeTab={
					tabsToDisplay[0] === props.windowData
						? {
								...props.windowData,
								id: props.windowData.tab_id,
								active: true,
							}
						: {
								url: tabsToDisplay[0].url,
								favicon: tabsToDisplay[0].favicon,
								title: tabsToDisplay[0].title,
								tab_id: tabsToDisplay[0].tab_id,
								id: tabsToDisplay[0].tab_id,
								active: false,
								chrome_id: "",
								chrome_window_id: "",
								email: "",
								expiry: 0,
								platform_type: "",
								screenshot: "",
								teacherIds: [],
							}
				}
				items={listModalItems}
				restrictedItems={[]}
				studentUsername={props.user.username}
				onClose={closeListModal}
				forceCloseTab={props.forceCloseTab}
				canCloseTab={props.canCloseTab}
				classIsActive={props.isClassActive}
				isMonitorMode={listModalIsMonitorMode}
				isStudentClaimed={props.isStudentClaimed}
			/>
			{tabsData.length > props.tabItemLimit ? (
				<div className="StudentTileContent_showMoreTabs">
					<button
						className=""
						onClick={() => openListModal(t("classroomDetails.openTabs"), props.windowData.background_tabs, listModalIsMonitorMode)}
					>
						{t("classroomDetails.showAllTabs", { count: tabsData.length })}
					</button>
				</div>
			) : null}
		</div>
	);
};

export default StudentTileTabView;
